import { useEffect, useState } from "react";
import { Radio, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { useTheme } from "@/context/ThemeContext";
import ThemePicker, { THEME_OPTIONS, themeLabel as label } from "@/components/dashboard/ThemePicker";

/**
 * ThemePanel — Admin Settings → Theme.
 *  1. Workgroup default: what new devices get until the person picks one.
 *  2. Global push: switch EVERY user's device to a theme right now (they can
 *     still change their own afterwards).
 *  3. Preview on this device.
 */
const ThemePanel = () => {
  const confirm = useConfirm();
  const { theme, setTheme } = useTheme();
  const [cfg, setCfg] = useState({ default_theme: "light", push: null });
  const [def, setDef] = useState("light");
  const [pushPick, setPushPick] = useState("orange");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/theme").then((r) => { setCfg(r.data); setDef(r.data.default_theme); }).catch(() => {}); }, []);

  const saveDefault = async () => {
    setBusy(true);
    try { const { data } = await api.put("/theme", { default_theme: def }); setCfg(data); toast.success(`Workgroup default is now ${label(def)}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const push = async () => {
    const ok = await confirm({ title: `Switch everyone to ${label(pushPick)}?`, description: "Every user's device changes to this theme the next time the portal loads. They can still pick their own theme afterwards from their settings.", confirmLabel: "Send to all users" });
    if (!ok) return;
    setBusy(true);
    try { const { data } = await api.post("/theme/push", { theme: pushPick }); setCfg(data); setTheme(pushPick); toast.success(`Theme change sent — everyone switches to ${label(pushPick)}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="theme-panel">
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-white">Workgroup default</h3>
          <p className="text-xs text-white/50">Applied to anyone who hasn't chosen a theme on their device, and used to draw this workgroup's <strong>sign-in screen</strong> so the look starts before login. The original look is saved as <strong>Default</strong>.</p>
        </div>
        <ThemePicker value={def} onPick={setDef} testPrefix="theme-default" />
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Current default: {label(cfg.default_theme)}</span>
          <button type="button" onClick={saveDefault} disabled={busy || def === cfg.default_theme} data-testid="theme-default-save" className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save default</button>
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-white/10 bg-white/[0.02] p-4">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white"><Radio className="h-4 w-4 text-[#8fa4f0]" /> Change every user's theme</h3>
          <p className="text-xs text-white/50">One-shot broadcast: all devices switch to the theme you pick on their next load. Users keep the freedom to switch back from their own settings.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {THEME_OPTIONS.map((o) => (
            <button key={o.key} type="button" onClick={() => setPushPick(o.key)} data-testid={`theme-push-${o.key}`} aria-pressed={pushPick === o.key} className={`rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest ${pushPick === o.key ? "border-[#1a3fd1] bg-[#1a3fd1] text-white" : "border-white/15 text-white/60 hover:text-white"}`}>{label(o.key)}</button>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="theme-push-last">{cfg.push ? `Last sent: ${label(cfg.push.theme)} · ${new Date(cfg.push.at).toLocaleString()}` : "Never sent"}</span>
          <button type="button" onClick={push} disabled={busy} data-testid="theme-push-send" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">Send to all users</button>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-white">Preview on this device</h3>
        <div className="flex flex-wrap gap-2">
          {THEME_OPTIONS.map((o) => <button key={o.key} type="button" onClick={() => setTheme(o.key)} data-testid={`theme-preview-${o.key}`} className={`rounded-md border px-3 py-1.5 text-xs ${theme === o.key ? "border-[#1a3fd1] bg-[#1a3fd1]/15 text-white" : "border-white/15 text-white/70 hover:border-white/30"}`}>{label(o.key)}</button>)}
        </div>
      </section>
    </div>
  );
};

export default ThemePanel;
